import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Search, Gift, CheckCircle, Clock, XCircle,
  Building2, Mail, Phone, MapPin, Briefcase, RefreshCw,
  ChevronDown, ChevronUp, StickyNote, Save, AlertCircle, AlertTriangle
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ProspectingOffersPanel } from '@/components/PioneerOffers';
import { ptDate } from '@/utils/pioneerDates';
import { PioneerList } from '@/components/PioneerList';
import { NifAuditPanel } from '@/components/admin/NifAuditPanel';
import { PioneerReviewPanel } from '@/components/admin/PioneerReviewPanel';
import { CompanyHubInfo, CompanyLogo, HUB_FILTERS } from '@/components/admin/CompanyHubInfo';

const CABO_VERDE_ISLANDS = [
  'Santiago', 'São Vicente', 'Sal', 'Fogo', 'Santo Antão',
  'Boa Vista', 'São Nicolau', 'Maio', 'Brava', 'Santa Luzia',
];

const FILTERS = [
  { key: 'all',            label: 'Todas',           color: 'bg-gray-100 text-gray-700' },
  { key: 'no_trial',       label: 'Sem trial',        color: 'bg-blue-100 text-blue-700' },
  { key: 'offer_sent',     label: 'Oferta enviada',   color: 'bg-amber-100 text-amber-700' },
  { key: 'offer_accepted', label: 'Trial activo',     color: 'bg-emerald-100 text-emerald-700' },
  { key: 'trial_active',   label: 'Trial a decorrer', color: 'bg-violet-100 text-violet-700' },
  { key: 'nif_duplicate',  label: 'NIF duplicado',    color: 'bg-red-100 text-red-700' },
  { key: 'founder',        label: 'Fundadoras',       color: 'bg-slate-900 text-amber-300' },
  { key: 'logo_pending',   label: 'Logótipos por aprovar', color: 'bg-amber-100 text-amber-700' },
];

function OfferStatusBadge({ company }) {
  if (company.trial_status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
        <CheckCircle className="h-3 w-3" /> Trial activo · {company.trial_unlimited ? 'vagas ilimitadas' : `${company.trial_remaining_credits} créditos`}
      </span>
    );
  }
  if (company.trial_status === 'expired') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
        <XCircle className="h-3 w-3" /> Trial expirado
      </span>
    );
  }
  if (company.offer_status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
        <Clock className="h-3 w-3" /> Oferta enviada · aguarda resposta
      </span>
    );
  }
  if (company.offer_status === 'accepted') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
        <CheckCircle className="h-3 w-3" /> Oferta aceite
      </span>
    );
  }
  if (company.offer_status === 'declined') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
        <XCircle className="h-3 w-3" /> Recusou
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">
      Sem oferta enviada
    </span>
  );
}

function NotesEditor({ companyId, initialNotes, onSaved }) {
  const [notes, setNotes] = useState(initialNotes || '');
  const [saving, setSaving] = useState(false);
  const dirty = notes !== (initialNotes || '');

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/companies/${companyId}/notes`, { notes });
      toast.success('Notas guardadas.');
      onSaved(notes);
    } catch {
      toast.error('Erro ao guardar notas.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`notes-section-${companyId}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <StickyNote className="h-3.5 w-3.5 text-amber-500" />
        <span className="text-xs font-semibold text-gray-600">Notas internas</span>
        {notes && <span className="text-[10px] text-gray-400">(visível apenas no admin)</span>}
      </div>
      <textarea
        className="w-full text-xs border border-gray-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-amber-300 bg-amber-50/50 placeholder:text-gray-300"
        rows={3}
        placeholder="Escreve aqui notas sobre o contacto… ex: Contactado por telefone em 24 Set, aguarda aprovação da chefia."
        value={notes}
        onChange={e => setNotes(e.target.value)}
        data-testid={`notes-input-${companyId}`}
      />
      {dirty && (
        <div className="flex justify-end mt-1">
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white gap-1"
            data-testid={`notes-save-btn-${companyId}`}>
            <Save className="h-3 w-3" />
            {saving ? 'A guardar…' : 'Guardar'}
          </Button>
        </div>
      )}
    </div>
  );
}

function CompanyCard({ company, onOfferSent }) {
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [localNotes, setLocalNotes] = useState(company.company_notes || '');

  const canSend = !company.trial_status && company.offer_status !== 'pending' && company.offer_status !== 'accepted';

  const sendOffer = async () => {
    if (!window.confirm(`Enviar oferta Pioneiro a ${company.company_name || company.email}?`)) return;
    setSending(true);
    try {
      await api.post(`/admin/companies/${company.id}/send-pioneer-offer`);
      toast.success(`Oferta enviada para ${company.company_name || company.email}!`);
      onOfferSent();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao enviar oferta.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const displayName = company.company_name || company.email;
  const joinDate = company.created_at
    ? new Date(company.created_at).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  const hasNotes = !!localNotes.trim();

  return (
    <Card className="p-4 hover:shadow-md transition-shadow" data-testid={`company-card-${company.id}`}>
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <CompanyLogo company={company} />

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start gap-2 mb-1">
            <span className="font-bold text-gray-900 truncate">{displayName}</span>
            {company.is_founder && (
              <span className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-900 text-amber-300 tracking-wide"
                title="Registada antes da política Pioneiro por convite — mantém as condições originais"
                data-testid={`founder-badge-${company.id}`}>
                Fundadora
              </span>
            )}
            {company.fiscal_nif && !company.nif_duplicate && (
              <Badge className="text-[10px] bg-blue-50 text-blue-600 border-blue-200">NIF ✓</Badge>
            )}
            {company.nif_duplicate && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300"
                title={`NIF ${company.fiscal_nif} já foi registado por outra conta. Este registo não recebe trial gratuito.`}
                data-testid={`nif-duplicate-badge-${company.id}`}>
                <AlertTriangle className="h-3 w-3" /> NIF duplicado — sem trial
              </span>
            )}
            {hasNotes && (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                <StickyNote className="h-2.5 w-2.5" /> Com notas
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-400 mb-2">
            <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{company.email}</span>
            {company.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{company.phone}</span>}
            {company.island && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{company.island}</span>}
            {company.sector && <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{company.sector}</span>}
            <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{company.jobs_count} vaga{company.jobs_count !== 1 ? 's' : ''} activa{company.jobs_count !== 1 ? 's' : ''}</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <OfferStatusBadge company={company} />
            <span className="text-[10px] text-gray-400">Registo: {joinDate}</span>
          </div>

          <CompanyHubInfo company={company} />


          {company.trial_started_at && (
            <p className="text-[11px] text-emerald-700 mt-1" data-testid={`company-trial-dates-${company.id}`}>
              Pioneiro: aceite a {ptDate(company.offer_accepted_at || company.trial_started_at)} · termina a {ptDate(company.trial_expires_at)}
            </p>
          )}

          {company.offer_sent_at && (
            <p className="text-[10px] text-amber-600 mt-1">
              Oferta enviada em {new Date(company.offer_sent_at).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}

          {/* Expandable notes */}
          <button
            onClick={() => setExpanded(e => !e)}
            className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 mt-2 transition-colors"
            data-testid={`toggle-notes-${company.id}`}>
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Fechar notas' : (hasNotes ? 'Ver / editar notas' : 'Adicionar notas')}
          </button>

          {expanded && (
            <NotesEditor
              companyId={company.id}
              initialNotes={localNotes}
              onSaved={setLocalNotes}
            />
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          {canSend && (
            <Button
              size="sm"
              onClick={sendOffer}
              disabled={sending}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5 text-xs"
              data-testid={`send-offer-btn-${company.id}`}>
              <Gift className="h-3.5 w-3.5" />
              {sending ? 'A enviar…' : 'Enviar Oferta Pioneiro'}
            </Button>
          )}
          {company.offer_status === 'pending' && (
            <Button
              size="sm"
              onClick={sendOffer}
              disabled={sending}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-1.5 text-xs"
              data-testid={`resend-offer-btn-${company.id}`}>
              <RefreshCw className="h-3.5 w-3.5" />
              {sending ? 'A reenviar…' : 'Reenviar Oferta'}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function AdminCompanies() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState(searchParams.get('search') || '');
  const [filter, setFilter]       = useState('all');
  const [hubFilter, setHubFilter] = useState(searchParams.get('filter') || '');
  const [islandFilter, setIslandFilter] = useState('all');
  const [total, setTotal]         = useState(0);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const params = new URLSearchParams();
      if (!['all', 'nif_duplicate', 'founder', 'logo_pending'].includes(filter)) params.set('status', filter);
      if (search.trim()) params.set('search', search.trim());
      params.set('limit', '1000');
      const { data } = await api.get(`/admin/companies?${params}`, { signal: controller.signal });
      let all = data.companies || [];
      if (filter === 'nif_duplicate') all = all.filter(c => c.nif_duplicate);
      if (filter === 'founder') all = all.filter(c => c.is_founder);
      if (filter === 'logo_pending') all = all.filter(c => c.pioneer_logo_status === 'pending');
      setCompanies(all);
      setTotal(all.length);
    } catch (e) {
      const msg = e?.code === 'ERR_CANCELED' ? 'O pedido demorou demasiado (>15s). Verifica a ligação.' : 'Erro ao carregar empresas.';
      setLoadError(msg);
      toast.error(msg);
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  // Ilhas disponíveis nos dados
  const availableIslands = [...new Set(
    companies.map(c => c.island).filter(Boolean).map(i => i.trim())
  )].sort();

  // Filtro local por ilha + filtros de estado (Pioneiro / Mural / NIF / novas)
  const hub = HUB_FILTERS.find((f) => f.key === hubFilter);
  const filtered = companies
    .filter(c => islandFilter === 'all' || (c.island || '').toLowerCase() === islandFilter.toLowerCase())
    .filter(c => !hub || hub.test(c));

  // Stats baseados nos dados filtrados por ilha
  const stats = {
    total:     filtered.length,
    noOffer:   filtered.filter(c => !c.trial_status && !c.offer_status).length,
    offerSent: filtered.filter(c => c.offer_status === 'pending').length,
    active:    filtered.filter(c => c.trial_status === 'active').length,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900" data-testid="companies-title">Empresas</h1>
            <p className="text-sm text-gray-500">Acompanhe cada empresa registada: vagas, candidaturas, compras, Pioneiro e Mural de Confiança</p>
          </div>
          <Button variant="outline" onClick={load} className="gap-2" disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
        </div>

        <NifAuditPanel />
        <PioneerReviewPanel />
        <PioneerList />
        <ProspectingOffersPanel />

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Card className="p-4 text-center">
            <div className="text-2xl font-black text-gray-800">{stats.total}</div>
            <div className="text-xs text-gray-500 mt-1">Total {islandFilter !== 'all' ? `em ${islandFilter}` : 'registadas'}</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-black text-blue-600">{stats.noOffer}</div>
            <div className="text-xs text-gray-500 mt-1">Sem oferta enviada</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-black text-amber-600">{stats.offerSent}</div>
            <div className="text-xs text-gray-500 mt-1">Aguardam resposta</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-black text-emerald-600">{stats.active}</div>
            <div className="text-xs text-gray-500 mt-1">Trial activo</div>
          </Card>
        </div>

        {/* Pesquisa */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm"
            placeholder="Pesquisar empresa ou email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="companies-search"
          />
        </div>

        {/* Filtros por estado Pioneiro / Mural / NIF */}
        <div className="mb-3 space-y-2 rounded-xl border border-slate-200 bg-white p-3" data-testid="company-hub-filters">
          {['Pioneiro', 'Mural', 'Registo', 'NIF'].map((g) => (
            <div key={g} className="flex flex-wrap items-center gap-1.5">
              <span className="w-16 text-[11px] font-bold uppercase tracking-wide text-slate-400">{g}</span>
              {HUB_FILTERS.filter((f) => f.group === g && !f.hidden).map((f) => (
                <button key={f.key} onClick={() => setHubFilter(hubFilter === f.key ? '' : f.key)}
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${hubFilter === f.key ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                  data-testid={`hub-filter-${f.key.replace(':', '-')}`}>{f.label}</button>
              ))}
            </div>
          ))}
          {hub && <button onClick={() => setHubFilter('')} className="text-xs text-blue-700 underline" data-testid="hub-filter-clear">Limpar filtro: {hub.label}</button>}
        </div>

        {/* Filtros de estado */}
        <div className="flex flex-wrap gap-2 mb-3">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all
                ${filter === f.key
                  ? 'border-transparent ' + f.color + ' shadow-sm'
                  : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`}
              data-testid={`filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Filtro por ilha */}
        {availableIslands.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5 pb-4 border-b border-gray-100">
            <span className="text-[11px] text-gray-400 flex items-center gap-1 self-center">
              <MapPin className="h-3 w-3" /> Ilha:
            </span>
            <button
              onClick={() => setIslandFilter('all')}
              className={`text-xs font-semibold px-3 py-1 rounded-full border transition-all
                ${islandFilter === 'all'
                  ? 'border-teal-400 bg-teal-50 text-teal-700 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`}
              data-testid="island-filter-all">
              Todas
            </button>
            {availableIslands.map(island => (
              <button
                key={island}
                onClick={() => setIslandFilter(island)}
                className={`text-xs font-semibold px-3 py-1 rounded-full border transition-all
                  ${islandFilter === island
                    ? 'border-teal-400 bg-teal-50 text-teal-700 shadow-sm'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`}
                data-testid={`island-filter-${island.toLowerCase().replace(' ', '-')}`}>
                {island}
              </button>
            ))}
          </div>
        )}

        {/* Lista */}
        {loadError && !loading && (
          <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid="companies-load-error">
            <div className="rounded-xl border border-red-200 bg-red-50 px-6 py-4 max-w-sm">
              <p className="text-red-700 font-semibold mb-1">Não foi possível carregar os dados.</p>
              <p className="text-red-600 text-sm mb-3">{loadError}</p>
              <button onClick={load} className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:bg-navy/90 transition">Tentar de novo</button>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-16 text-gray-400">
            <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin opacity-40" />
            <p>A carregar empresas…</p>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <Card className="p-12 text-center text-gray-400">
            <Building2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">Nenhuma empresa encontrada</p>
            {(search || islandFilter !== 'all') && <p className="text-sm mt-1">Tenta alterar os filtros</p>}
          </Card>
        )}

        {!loading && filtered.length > 0 && (
          <div className="space-y-3">
            {filtered.map(c => (
              <CompanyCard key={c.id} company={c} onOfferSent={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
